import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';

const copy = {
  en: {
    heading: 'Say it now, not later.',
    subheading: 'The people you love deserve to hear it while they still can.',
    cta: 'Create Your Free Message',
  },
  es: {
    heading: 'Dilo ahora, no después.',
    subheading: 'Las personas que amas merecen escucharlo mientras aún pueden.',
    cta: 'Crea Tu Mensaje Gratis',
  },
  pt: { heading: 'Diga agora, não depois.', subheading: 'As pessoas que você ama merecem ouvir enquanto ainda podem.', cta: 'Crie Sua Mensagem Grátis' },
  fr: { heading: 'Dites-le maintenant, pas plus tard.', subheading: 'Les personnes que vous aimez méritent de l\'entendre tant qu\'elles le peuvent encore.', cta: 'Créez Votre Message Gratuit' },
  ar: { heading: 'قلها الآن، وليس لاحقاً.', subheading: 'الأشخاص الذين تحبهم يستحقون سماعها بينما لا يزال بإمكانهم.', cta: 'أنشئ رسالتك المجانية' },
  ja: { heading: '後ではなく、今伝えましょう。', subheading: '愛する人たちはまだ聞けるうちに聞く権利があります。', cta: '無料メッセージを作成' },
  ko: { heading: '나중이 아닌, 지금 말하세요.', subheading: '사랑하는 사람들은 아직 들을 수 있을 때 들을 자격이 있습니다.', cta: '무료 메시지 만들기' },
  id: { heading: 'Katakan sekarang, bukan nanti.', subheading: 'Orang-orang yang Anda cintai layak mendengarnya selagi mereka masih bisa.', cta: 'Buat Pesan Gratis Anda' },

  hi: {
    heading: 'अभी कहो, बाद में नहीं।',
    subheading: 'जिन लोगों से आप प्यार करते हैं, वे इसे तब सुनने के हकदार हैं जब वे अभी भी सुन सकते हैं।',
    cta: 'अपना निःशुल्क संदेश बनाएँ',
  },
};

export function FinalCTASection() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section className="relative py-28 md:py-36 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full blur-[140px]"
          style={{ background: 'hsl(var(--primary) / 0.08)' }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="container relative z-10 max-w-3xl mx-auto text-center">
        <motion.h2
          className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold billboard-flash mb-6 !leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {t.heading}
        </motion.h2>
        <motion.p
          className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {t.subheading}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Link to="/try">
            <Button size="lg" className="gap-2 text-lg px-10 py-7 rounded-2xl shadow-lg shadow-primary/25 group">
              <Heart className="h-5 w-5 group-hover:scale-110 transition-transform" fill="currentColor" />
              {t.cta}
            </Button>
          </Link>
          <p className="mt-5 text-sm text-muted-foreground italic">
            Start with yourself — then share it with someone who matters.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
